import type { ComponentType, VNode } from 'preact';

import type { TemplateContextMap, TemplateContextValue } from 'Components/types';
import type { AnyWidget } from 'Templates/widgets';
import { pickObjectKeys } from 'Utils/array';

const facetPanelKeys = [
  'fields',
  'name',
  'ignoreFields',
  'showAllAlways',
  'disableCollapse',
  'initCollapsed',
  'initExpandedFacets',
  'initCollapsedFacets',
  'valuesCountToShowFilterInput',
];

const searchBoxKeys = [
  'name',
  'onDropdownItemsReceived',
  'onSubmit',
  'disableDropdown',
  'redirectUrl',
  'useSuggestionsFilter',
];

const vehicleWidgetKeys = [
  'name',
  'columnBreakpoint',
  'doNotRedirectOnVehicleSelect',
  'initCollapsed',
  'isAlwaysColumnLayout',
  'isAutoVehicleSelectionDisabled',
  'isVehicleSelectionIsolated',
  'facetToggleConfig',
  'fields',
  'globalVehicleDiscardEnabled',
  'redirectUrl',
  'useNativeDropdown',
  'useSearchableDropdown',
  'autoOpenDropdowns',
  'useAutoSyncVisualizer',
  'autoSyncConfig',
];

const widgetToKeysMap: Record<string, readonly string[]> = {
  DialogButton: ['dialogName', 'onClick', 'metadata'],
  FacetPanel: facetPanelKeys,
  FacetBar: ['fields', 'initCollapsed', 'useNativeDropdown', 'minFacetCount', 'showHitCount'],
  FacetToggle: ['toggledFacet', 'fieldsForVisibility', 'initToggled', 'isInverted', 'saveLastState'],
  FilterChips: ['fields', 'ignoreFields'],
  FitmentTable: ['initCollapsed', 'columnBreakpoint', 'alwaysShowedCount'],
  Garage: [
    'name',
    'doNotRedirectOnVehicleSelect',
    'isAutoVehicleSelectionDisabled',
    'columnBreakpoint',
    'redirectUrl',
    'useNativeDropdown',
    'useVehicleWidget',
  ],
  SingleVehicleGarage: ['withCurrentVehicle'],
  RequestPanel: [
    'name',
    'isAutoRedirectDisabled',
    'initCollapsed',
    'inputFields',
    'onSubmit',
    'discardVehicleOnSubmit',
    'requiredFields',
    'redirectUrl',
    'requestExtra',
    'selectFields',
    'shouldBeIsolated',
    'mayDiscardIsolatedSelection',
    'useNativeDropdown',
    'useSearchableDropdown',
    'autoOpenDropdowns',
    'localStorageKey',
  ],
  ProductData: ['fields'],
  RelatedItems: [
    'name',
    'count',
    'getSelection',
    'onItemsRendered',
    'requestExtra',
    'sort',
    'useManualItems',
  ],
  BuyItemsTogether: ['name', 'mode', 'count', 'onItemsRendered'],
  SearchBox: searchBoxKeys,
  SearchBoxDialogButton: searchBoxKeys,
  SearchHeader: [
    'facetToggleConfig',
    'pageSizes',
    'pageSizeSelectClass',
    'doNotHideOnNoResults',
    'sortSelectClass',
  ],
  SearchResult: ['instantInfiniteScroll', 'infiniteScrollDisabled', 'updateCallback'],
  VehicleWidget: [...vehicleWidgetKeys, 'isAlwaysActive'],
  VehicleTabs: [...vehicleWidgetKeys, 'tabs'],
  VehicleTiles: ['name', 'doNotRedirectOnVehicleSelect'],
  VerifyFitment: [
    'name',
    'initCollapsed',
    'isAlwaysColumnLayout',
    'isAutoVehicleSelectionDisabled',
    'globalVehicleDiscardEnabled',
    'columnBreakpoint',
    'useNativeDropdown',
    'useSearchableDropdown',
  ],
  FacetTiles: [
    'columnBreakpoint',
    'excludeFieldPreselection',
    'facetField',
    'initCollapsed',
    'isAlwaysActive',
    'isMultiLevel',
    'isVehicleWidgetDisabled',
    'useFilterMode',
    'view',
    'useNativeDropdown',
    'useSearchableDropdown',
  ],
  SearchBoxDialog: searchBoxKeys,
  FacetDialog: facetPanelKeys,
  VehicleWidgetDialog: [
    'columnBreakpoint',
    'doNotRedirectOnVehicleSelect',
    'isAutoVehicleSelectionDisabled',
    'isAlwaysColumnLayout',
    'fields',
    'globalVehicleDiscardEnabled',
    'redirectUrl',
    'useNativeDropdown',
    'useSearchableDropdown',
  ],
  SimpleColorPaletteDialog: ['field'],
  ColorExtractorDialog: ['field'],
  VehicleInfo: ['initCollapsed', 'columnBreakpoint'],
  WheelTireVehicleInfo: ['initCollapsed', 'useAutoSyncVisualizer', 'autoSyncConfig'],
  AutoSyncVisualization: ['config'],
  AutoSyncVisualizationDialog: ['config'],
};

type WidgetComponentProps = TemplateContextMap & {
  items: TemplateContextValue;
  template: TemplateContextValue;
};

type WithTemplateComponent<T extends AnyWidget> = T extends AnyWidget
  ? Omit<T, 'component'> & {
      component?: ComponentType<WidgetComponentProps>;
    }
  : never;

type TemplateWidgetConfig = WithTemplateComponent<AnyWidget> & {
  name: string;
  type: AnyWidget['type'];
};

function makeTemplateApplicator(
  widget: TemplateWidgetConfig,
):
  | ((
      template: TemplateContextValue,
      context: { widgetName: string; items: TemplateContextValue },
    ) => VNode<object>)
  | null {
  const Component = widget.component;
  if (!Component) {
    return null;
  }

  const propKeys = widgetToKeysMap[widget.type] ?? [];

  return (template, { widgetName, items }) => {
    const props: WidgetComponentProps = {
      template,
      items,
      ...pickObjectKeys(widget, propKeys as readonly (keyof TemplateWidgetConfig)[]),
    };

    if (widget.type === 'VehicleWidget') {
      const [nameRoot] = widget.name.split('_');
      props.isAlwaysActive =
        widget.isAlwaysActive ??
        (nameRoot === 'HeaderVehicleWidget' ||
          nameRoot === 'HomeVehicleWidget' ||
          nameRoot === 'VehicleSpecs');
    }

    return <Component {...props} key={widgetName} />;
  };
}

export type { WidgetConfig } from './widgetProcessor';

export default makeTemplateApplicator;
